/**
 * Signature help provider helper for INSERT INTO ... VALUES statements.
 *
 * When the cursor is inside a `VALUES (...)` tuple, this returns a Monaco-
 * compatible `SignatureHelp` whose single signature is the parenthesized
 * column list, with `activeParameter` set to the column index corresponding
 * to the cursor position. Returns null when the cursor is not inside a VALUES
 * tuple or when column names cannot be resolved.
 *
 * This is the primary UX for "which column should I type next" on long
 * INSERTs, because the Monaco signature-help popup is a floating widget and
 * is NOT subject to the inlay-hint rendering cap on word-wrapped lines.
 */

import {
  parseInsertPrefix,
  findEnclosingTupleOpenParen,
  findActiveValueIndex,
} from './insertHintParser';
import { resolveInsertColumnNames } from './insertInlayHintsProvider';
import type { DatabaseEngineId } from '@/core/engines';

/**
 * Structurally compatible with Monaco's `languages.SignatureHelp`:
 *   - `signatures`: maps to Monaco's `SignatureInformation[]`.
 *   - `activeSignature`: always `0` — we only ever emit one signature.
 *   - `activeParameter`: matches Monaco's `number` field.
 *   - `parameters[i].label: [number, number]` is Monaco's preferred form;
 *     it highlights a substring of the signature label (not a separate chip),
 *     which is exactly the "column name within (a, b, c)" UX we want.
 *
 * The bundled Monaco version requires `provideSignatureHelp` to return
 * `SignatureHelpResult = { value: SignatureHelp; dispose(): void }` rather
 * than the bare `SignatureHelp` — so the adapter in `SqlMonaco.tsx` wraps
 * this value in `{ value, dispose: () => {} }` before returning it. Keeping
 * this helper's return type bare lets the unit tests stay pure and
 * Monaco-import-free; the wrapping happens only at the provider boundary.
 */
export type InsertSignatureHelp = {
  signatures: Array<{
    label: string;
    parameters: Array<{ label: [number, number] }>;
  }>;
  activeSignature: 0;
  activeParameter: number;
};

/**
 * Build the signature label `(col1, col2, col3)` and the character ranges for
 * each parameter within that label.
 */
function buildSignature(columns: string[]): {
  label: string;
  parameters: Array<{ label: [number, number] }>;
} {
  const parameters: Array<{ label: [number, number] }> = [];
  let label = '(';
  for (let i = 0; i < columns.length; i++) {
    if (i > 0) label += ', ';
    const start = label.length;
    label += columns[i];
    const end = label.length;
    parameters.push({ label: [start, end] });
  }
  label += ')';
  return { label, parameters };
}

export async function computeInsertSignatureHelp(
  text: string,
  cursorOffset: number,
  connectionId: string | undefined,
  database: string | undefined,
  engineId: DatabaseEngineId,
): Promise<InsertSignatureHelp | null> {
  const prefix = parseInsertPrefix(text);
  if (!prefix) return null;

  // Cursor is still in the INSERT prefix (e.g. inside the optional column list).
  if (cursorOffset < prefix.valuesOffset) return null;

  const openParen = findEnclosingTupleOpenParen(text, prefix.valuesOffset, cursorOffset);
  if (openParen === -1) return null;

  const rawActive = findActiveValueIndex(text, openParen, cursorOffset);

  const columns = await resolveInsertColumnNames(
    prefix.tableName,
    prefix.columnNames,
    connectionId,
    database,
    engineId,
  );
  // Belt-and-suspenders: `resolveInsertColumnNames` usually returns null when
  // it can't find columns, but an `INSERT INTO t () VALUES ...` (legal-ish
  // explicit empty column list) would parse to [], and with 0 columns
  // `activeParameter = -1` would be invalid. Bail out rather than emit a
  // broken signature.
  if (!columns || columns.length === 0) return null;

  // Clamp — if the user has typed more values than there are columns, keep the
  // active marker on the last column so the popup still shows something useful.
  const activeParameter = Math.min(rawActive, columns.length - 1);

  const signature = buildSignature(columns);

  return {
    signatures: [signature],
    activeSignature: 0,
    activeParameter,
  };
}
